import { bookingConstants } from '../_constants';
import { hairdresserConstants } from '../_constants/hairdresser.constants';
import { userConstants } from '../_constants/user.constants';

// Holds the lifecycle of the global NewBookingPanel so it can survive route
// changes AND a browser reload (redux-persist writes this slice to
// localStorage automatically — it's not in the persist blacklist).
//
// Shape:
//   active       — there's an in-progress booking draft right now
//   isMinimised  — render the pill (true) vs. the full panel (false)
//   draft        — last snapshot of the form fields; used to seed the panel
//                  on first mount and to rehydrate after reload
//   meta         — context captured at open-time so the panel can render
//                  even on routes that don't have access to the diary's
//                  derived staff list or the current salon currency
const initialState = {
  active: false,
  isMinimised: false,
  draft: {
    staffId: '',
    bookingDate: '',
    startMinutes: null,
    selectedClient: null,
    // Details for the add-client form when Bailey books someone new.
    newClient: null,
    services: [],
    bookingNote: '',
    // Repeat bookings: the Repeat row's value when the panel opens set to repeat
    repeat: null,
  },
  meta: {
    staffList: [],
    currencySymbol: '£',
  },
};

export function minimisedBooking(state = initialState, action) {
  switch (action.type) {
    case bookingConstants.MINIMISED_BOOKING_OPEN: {
      const p = action.payload || {};
      return {
        active: true,
        isMinimised: false,
        draft: {
          staffId: p.staffId || '',
          bookingDate: p.bookingDate || '',
          startMinutes: p.startMinutes != null ? p.startMinutes : null,
          selectedClient: p.rebookClient || null,
          newClient: p.newClient || null,
          services: p.rebookServices || [],
          bookingNote: '',
          repeat: p.repeat || null,
        },
        meta: {
          staffList: Array.isArray(p.staffList) ? p.staffList : [],
          currencySymbol: p.currencySymbol || '£',
        },
      };
    }

    case bookingConstants.MINIMISED_BOOKING_SET_MINIMISED: {
      const p = action.payload || {};
      return {
        ...state,
        isMinimised: !!p.isMinimised,
        // When minimising the panel passes its current form snapshot so we
        // capture the latest values for reload survival. When restoring it
        // passes nothing and we keep whatever draft is in state.
        draft: p.draft ? { ...state.draft, ...p.draft } : state.draft,
      };
    }

    case bookingConstants.MINIMISED_BOOKING_UPDATE_SLOT: {
      // User clicked another diary slot while the panel was minimised.
      // Re-point at the new slot AND restore the panel, preserving the
      // existing client/services/note. Keeps the minimised draft "alive"
      // through multiple slot retries.
      const p = action.payload || {};
      return {
        ...state,
        active: true,
        isMinimised: false,
        draft: {
          ...state.draft,
          staffId: p.staffId != null ? p.staffId : state.draft.staffId,
          bookingDate: p.bookingDate != null ? p.bookingDate : state.draft.bookingDate,
          startMinutes: p.startMinutes != null ? p.startMinutes : state.draft.startMinutes,
        },
      };
    }

    case bookingConstants.MINIMISED_BOOKING_CLOSE:
      return initialState;

    // Any time the user crosses the role boundary (business <-> client) or
    // logs out, drop the in-flight draft. A booking started on the
    // business side is irrelevant on the client side; persisting it via
    // redux-persist would otherwise show the pill on every client page.
    case hairdresserConstants.HAIRDRESSER_SWITCH_SUCCESS:
    case userConstants.LOGOUT_SUCCESS:
      return initialState;

    default:
      return state;
  }
}
